import {createSlice, createSelector} from "@reduxjs/toolkit";

const initialState = {
    semiFinishedProductsForModalArray: [],
    semiFinishedProductsWeight: 0,
    semiFinishedProductsWeightNetto: 0,
    semiFinishedProductsCost: 0,
    semiFinishedProductEnergy: 0,
    semiFinishedProductsFats: 0,
    semiFinishedProductsProteins: 0,
    semiFinishedProductsCarbohydrates: 0,
    trigger: 0,
};

const semiFinishedProductSlice = createSlice({
    name: "semiFinishedProductSlice",
    initialState,
    reducers: {
        changeTrigger: (state) => {
            state.trigger += 1;
        },
        updateArray: (state, action) => {
            state.semiFinishedProductsForModalArray = action.payload;
            semiFinishedProductSlice.caseReducers.recalculateTotals(state);
        },
        addDishProductsForModal: (state, action) => {
            state.semiFinishedProductsForModalArray.push(action.payload);
            semiFinishedProductSlice.caseReducers.recalculateTotals(state);
        },
        updateSemiFinishedProductsForModal: (state, action) => {
            state.semiFinishedProductsForModalArray = action.payload;
            semiFinishedProductSlice.caseReducers.recalculateTotals(state);
        },
        removeDishProductForModal: (state, action) => {
            state.semiFinishedProductsForModalArray = state.semiFinishedProductsForModalArray.filter(
                (product) => product.id !== action.payload
            );
            semiFinishedProductSlice.caseReducers.recalculateTotals(state);
        },
        clearArray: (state) => {
            state.semiFinishedProductsForModalArray = [];
            state.semiFinishedProductsWeight = 0;
            state.semiFinishedProductsWeightNetto = 0;
            state.semiFinishedProductsCost = 0;
            state.semiFinishedProductEnergy = 0;
            state.semiFinishedProductsFats = 0;
            state.semiFinishedProductsProteins = 0;
            state.semiFinishedProductsCarbohydrates = 0;
        },
        recalculateTotals: (state) => {
            const array = state.semiFinishedProductsForModalArray;

            state.semiFinishedProductsWeight = array.reduce(
                (sum, product) => sum + (parseFloat(product.weight) || 0), 0
            );

            state.semiFinishedProductsWeightNetto = array.reduce(
                (sum, product) => {
                    const weight = parseFloat(product.weight) || 0;
                    
                    // Check if this is a manually added item with pre-calculated weightNetto
                    if (product.weightNetto !== undefined) {
                        return sum + (parseFloat(product.weightNetto) || 0);
                    }
                    
                    // Handle API data structure - calculate weightNetto based on product's ratio
                    if (product.product?.product) {
                        const prodWeight = parseFloat(product.product.product.weight) || 0;
                        const prodWeightNetto = parseFloat(product.product.product.weightNetto) || 0;
                        const nettoRatio = prodWeight > 0 ? prodWeightNetto / prodWeight : 0;
                        return sum + (weight * nettoRatio);
                    }
                    
                    // Handle direct product reference
                    if (product.product) {
                        const prodWeight = parseFloat(product.product.weight) || 0;
                        const prodWeightNetto = parseFloat(product.product.weightNetto) || 0;
                        const nettoRatio = prodWeight > 0 ? prodWeightNetto / prodWeight : 0;
                        return sum + (weight * nettoRatio);
                    }
                    
                    // Fallback - assume 100% ratio if no ratio data available
                    return sum + weight;
                }, 0
            );

            state.semiFinishedProductsCost = parseFloat(
                array.reduce((sum, product) => sum + (parseFloat(product.cost) || 0), 0).toFixed(2)
            );

            state.semiFinishedProductEnergy = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is a manually added product with pre-calculated values
                    if (product.product && typeof product.product.energy === 'number') {
                        return sum + (parseFloat(product.product.energy) || 0);
                    }
                    // Handle API data structure: product.product.energy (from backend) or fallback to direct values
                    const energy = parseFloat(product.product?.product?.energy ?? product.product?.energy ?? product.energy) || 0;
                    const weight = parseFloat(product.weight) || 0;
                    return sum + (energy * weight / 100);
                }, 0).toFixed(2)
            );

            state.semiFinishedProductsFats = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is a manually added product with pre-calculated values
                    if (product.product && typeof product.product.fats === 'number') {
                        return sum + (parseFloat(product.product.fats) || 0);
                    }
                    // Handle API data structure: product.product.fats (from backend) or fallback to direct values
                    const fats = parseFloat(product.product?.product?.fats ?? product.product?.fats ?? product.fats) || 0;
                    const weight = parseFloat(product.weight) || 0;
                    return sum + (fats * weight / 100);
                }, 0).toFixed(2)
            );

            state.semiFinishedProductsProteins = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is a manually added product with pre-calculated values
                    if (product.product && typeof product.product.proteins === 'number') {
                        return sum + (parseFloat(product.product.proteins) || 0);
                    }
                    // Handle API data structure: product.product.proteins (from backend) or fallback to direct values
                    const proteins = parseFloat(product.product?.product?.proteins ?? product.product?.proteins ?? product.proteins) || 0;
                    const weight = parseFloat(product.weight) || 0;
                    return sum + (proteins * weight / 100);
                }, 0).toFixed(2)
            );

            state.semiFinishedProductsCarbohydrates = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is a manually added product with pre-calculated values
                    if (product.product && typeof product.product.carbohydrates === 'number') {
                        return sum + (parseFloat(product.product.carbohydrates) || 0);
                    }
                    // Handle API data structure: product.product.carbohydrates (from backend) or fallback to direct values using both carbohydrates and carbs field names
                    const carbs = parseFloat(product.product?.product?.carbohydrates ?? product.product?.product?.carbs ?? product.product?.carbohydrates ?? product.product?.carbs ?? product.carbohydrates ?? product.carbs) || 0;
                    const weight = parseFloat(product.weight) || 0;
                    return sum + (carbs * weight / 100);
                }, 0).toFixed(2)
            );
        },
    },
});

const arrayFormattedProducts = createSelector(
    (state) => state.semiFinishedProducts.semiFinishedProductsForModalArray,
    (array) =>
        array.map((product, index) => ({
            index: index,
            productId: product.productId ?? product.id,
            weight: product.weight,
            weightNetto: product.weightNetto,
            cost: product.cost,
            price: product.price,
        }))
);

const {reducer: semiFinishedProductsReducer, actions} = semiFinishedProductSlice;
const semiFinishedProductsActions = {...actions};

export {
    semiFinishedProductsReducer,
    semiFinishedProductsActions,
    arrayFormattedProducts
};

